import React, { useState, useEffect } from 'react';
import { Card, Select, FormLayout, Popover, Button, TextField, Text, InlineStack } from '@shopify/polaris';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatDate, formatToCurrency } from '../../../../shared/components/functions';
import { useToggle } from '../../../../shared/useToogle';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatAnalyticsPeriodLabel } from './periodFormatting';

export default function CostDistribution() {
  const [data, setData] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [groupBy, setGroupBy] = useState('YEAR');
  const [popoverActive, setPopoverActive] = useToggle();
  const [startDate, setStartDate] = useState(formatDate(new Date(new Date().setFullYear(new Date().getFullYear() - 10))));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const activator = (
    <Button onClick={setPopoverActive} disclosure>
      Customize
    </Button>
  );

  const options = [
    { label: 'Month', value: 'MONTH' },
    { label: 'Year', value: 'YEAR' },
    { label: 'Day', value: 'DAY' },
    { label: 'Quarter', value: 'QUARTER' }
  ];

  const typeOptions = [{ label: 'All Categories', value: '' }, ...[...types].sort((a, b) => a.localeCompare(b)).map((t) => ({ label: t, value: t }))];

  async function fetchData() {
    const response = await fetch(`/api/metrics/cost-distribution?groupBy=${groupBy}&startDate=${startDate}&endDate=${endDate}`);
    const rawData = await response.json();
    console.log('Cost Distribution Data:', rawData);

    // Transform data for Recharts
    // From: [{ period: "2023", total: 1000, BestellingType: "TypeA" }, ...]
    // To: [{ name: "2023", TypeA: 1000, ... }]

    // Calculate total for each type to determine order
    const typeTotals = rawData.reduce((acc, curr) => {
      const type = curr.BestellingType || 'Unknown';
      acc[type] = (acc[type] || 0) + (curr.total || 0);
      return acc;
    }, {});

    // Sort types by total value descending
    const uniqueTypes = [...new Set(rawData.map((item) => item.BestellingType || 'Unknown'))].sort((a, b) => {
      return (typeTotals[b] || 0) - (typeTotals[a] || 0);
    });
    setTypes(uniqueTypes);

    const transformedData = rawData.reduce((acc, curr) => {
      const period = String(curr.period);
      const type = curr.BestellingType || 'Unknown';
      const total = curr.total;

      let existing = acc.find((item) => item.name === period);
      if (!existing) {
        existing = { name: period };
        acc.push(existing);
      }
      existing[type] = total;
      return acc;
    }, []);

    // Sort by period (assuming year/month string sort works effectively for "YYYY" or "YYYY-MM")
    transformedData.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    console.log('Cost Distribution Transformed:', transformedData);

    setData(transformedData);
  }

  useEffect(() => {
    fetchData();
  }, [groupBy, startDate, endDate]);

  const customize_graph = (
    <Popover active={popoverActive} activator={activator} onClose={setPopoverActive} ariaHaspopup={false} sectioned>
      <FormLayout>
        <Select label="Group by" options={options} onChange={setGroupBy} value={groupBy} />
        <TextField id="start_date" maxLength={60} type="date" autoComplete="off" label="Start Date" value={startDate} onChange={setStartDate} />
        <TextField id="end_date" maxLength={60} type="date" autoComplete="off" label="End Date" value={endDate} onChange={setEndDate} />
      </FormLayout>
    </Popover>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card>
          <Text className="label">{`Period : ${formatAnalyticsPeriodLabel(label)}`}</Text>
          {payload
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => (
              <p key={index} style={{ color: entry.color }} className="label">
                {`${entry.name}: ${formatToCurrency(entry.value)}`}
              </p>
            ))}
        </Card>
      );
    }
    return null;
  };

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F', '#FFBB28', '#FF8042', '#a83279', '#32a852'];

  return (
    <Card>
      <InlineStack align="space-between">
        <CardHeading title="Cost Category Distribution" />
        <InlineStack gap="200">
          <div style={{ width: '200px' }}>
            <Select label="Filter Type" labelHidden options={typeOptions} value={selectedType} onChange={setSelectedType} />
          </div>
          {customize_graph}
        </InlineStack>
      </InlineStack>

      <div style={{ width: '100%', height: '750px', marginTop: '20px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={500}
            height={300}
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tickFormatter={formatAnalyticsPeriodLabel} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {types.map((type, index) => {
              if (selectedType && selectedType !== type) return null;
              return <Bar key={type} dataKey={type} stackId="a" fill={colors[index % colors.length]} />;
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
